import { Injectable } from '@angular/core';
import {
  API_V1_point,
  API_V4_points_workspace,
  API_point,
  API_point_additional_details,
  API_point_custom_field_sum,
  API_point_group_size,
  API_point_relation,
  API_point_relations,
  API_point_search_all,
  API_point_search_site,
  API_point_simple_update,
  API_points_bulk,
  API_points_bulk_simple_update,
  API_points_create,
  API_points_create_with_relationships,
  API_points_lightweight,
  API_points_selected,
  API_points_transfer_check,
  API_points_transfer_copy,
  API_points_transfer_move,
  API_points_transfer_size,
  API_points_users_with_access,
  API_points_transfer_copy_multiple,
} from '@core/api/paths';
import { TGuid } from '@core/helpers';
import { ApiService } from '@core/http';
import { Observable } from 'rxjs';
import {
  TRelatedPoint,
  TRelatedPointPayload,
} from 'src/app/project/components/related-points/related-points.consts';
import {
  TPointSearchResponse,
  TSearchResponseDTO,
} from 'src/app/project/modules/points/point-search/point-search.consts';
import {
  TPointCustomFieldAllSumsResponse,
  TPointCustomFieldQuery,
} from 'src/app/project/modules/points/points-custom-fields-sum/points-custom-fields-sum.const';
import { TPointLightweight } from 'src/app/project/modules/points/points.model';
import { EAccessField } from 'src/app/project/shared/enums/access-field.enum';
import { TCheckTransferResponse } from 'src/app/project/view-models/check-transfer-response.model';
import { TPointsSizeResponse } from 'src/app/project/view-models/points-size-response.model';
import { TPointResponse, TPointUpdate } from '../../../view-models/point-response.model';
import { TPointDetails } from '../../../view-models/points-details-response.model';
import { TPointPaginationResponse } from '../../../view-models/points-pagination-response.model';
import {
  TCheckTargetSiteRequest,
  TNewPoint,
  TNewPointWithRelationships,
  TPointSizesMultipleRequest,
  TPointSizesRequest,
  TUpdatePointsRequest,
} from './points-requests.model';

@Injectable({
  providedIn: 'root',
})
export class PointsApiProviderService {
  constructor(private apiService: ApiService) {}

  deletePoints(body: string[]): Observable<void> {
    const url = API_points_bulk();

    return this.apiService.delete<void>(url, body);
  }

  checkTargetSiteForTransfer(body: TCheckTargetSiteRequest): Observable<TCheckTransferResponse> {
    const url = API_points_transfer_check();

    return this.apiService.post<TCheckTransferResponse>(url, body);
  }

  fetchPointDetails(_id: string): Observable<TPointDetails> {
    const url = API_point_additional_details(_id);

    return this.apiService.get<TPointDetails>(url);
  }

  fetchUsersWithAccessToPoint(
    pointId: TGuid,
    field: EAccessField,
    customFieldId?: TGuid,
  ): Observable<any> {
    return this.apiService.get<string>(API_points_users_with_access(pointId, field, customFieldId));
  }

  movePoints(body: TPointSizesRequest): Observable<string> {
    const url = API_points_transfer_move();

    return this.apiService.post<string>(url, body);
  }

  copyPoints(body: TPointSizesRequest): Observable<string> {
    const url = API_points_transfer_copy();

    return this.apiService.post<string>(url, body);
  }

  copyMultiplePoints(body: TPointSizesMultipleRequest, targetSiteId: string): Observable<string> {
    const url = API_points_transfer_copy_multiple(targetSiteId);

    return this.apiService.post<string>(url, body);
  }

  deletePoint(_id: string): Observable<null> {
    const url = API_V1_point(_id);

    return this.apiService.delete(url);
  }

  updatePoints(body: TUpdatePointsRequest): Observable<TPointResponse[]> {
    const url = API_points_bulk_simple_update();

    return this.apiService.put<TPointResponse[]>(url, body);
  }

  updatePointField(pointId: string, body: TPointUpdate): Observable<TPointResponse> {
    const url = API_point_simple_update(pointId);

    return this.apiService.put<TPointResponse>(url, body);
  }

  fetchPointSizes(body: TPointSizesRequest): Observable<TPointsSizeResponse> {
    const url = API_points_transfer_size();

    return this.apiService.post<TPointsSizeResponse>(url, body);
  }

  /**
   * @deprecated Use `createPointWithRelationships` instead. This method calls the
   * v3 endpoint which does not accept `relationships` in the payload. It will be
   * removed once the `RELATED_POINTS` feature flag is fully rolled out.
   */
  createPoint(workspaceId: string, body: TNewPoint): Observable<TPointResponse> {
    const url = API_points_create(workspaceId);

    return this.apiService.post<TPointResponse>(url, body);
  }

  createPointWithRelationships(
    workspaceId: string,
    body: TNewPointWithRelationships,
  ): Observable<TPointResponse> {
    const url = API_points_create_with_relationships(workspaceId);

    return this.apiService.post<TPointResponse>(url, body);
  }

  fetchPoint(_id: string): Observable<TPointResponse> {
    const url = API_point(_id);

    return this.apiService.get<TPointResponse>(url);
  }

  fetchPoints(workspaceId: string, pageNumber: string): Observable<TPointPaginationResponse> {
    const url = API_V4_points_workspace(workspaceId, pageNumber);

    return this.apiService.get<TPointPaginationResponse>(url);
  }

  fetchPointsById(pointList: string[]): Observable<TPointResponse[]> {
    const url = API_points_selected();

    return this.apiService.post<TPointResponse[]>(url, pointList);
  }

  fetchPointsLightweight(workspaceId: string): Observable<TPointLightweight[]> {
    const url = API_points_lightweight(workspaceId);

    return this.apiService.get<TPointLightweight[]>(url);
  }

  searchAllPoints(query: TSearchResponseDTO): Observable<TPointSearchResponse> {
    const url = API_point_search_all();

    return this.apiService.post<TPointSearchResponse>(url, query);
  }

  searchSitePoints(
    query: TSearchResponseDTO,
    workspaceId: string,
  ): Observable<TPointSearchResponse> {
    const url = API_point_search_site(workspaceId);

    return this.apiService.post<TPointSearchResponse>(url, query);
  }

  fetchCustomFieldSum(query: TPointCustomFieldQuery): Observable<TPointCustomFieldAllSumsResponse> {
    const url = API_point_custom_field_sum();

    return this.apiService.post<TPointCustomFieldAllSumsResponse>(url, query);
  }

  fetchGroupSizes(query: TPointCustomFieldQuery, workspaceId?: string): Observable<unknown> {
    const url = API_point_group_size(workspaceId);

    return this.apiService.post<TPointCustomFieldAllSumsResponse>(url, query);
  }

  addRelation(query: TRelatedPointPayload, pointId: string): Observable<TRelatedPoint> {
    const url = API_point_relations(pointId);

    return this.apiService.post<TRelatedPoint>(url, query);
  }

  deleteRelation(pointId: string, relatedPointId: string): Observable<never> {
    const url = API_point_relation(pointId, relatedPointId);

    return this.apiService.delete<never>(url);
  }
}
